// ─── Forfaits — activation des fonctions (Core) ──────────────────
// Feuille de gestion : liste les fonctions et sous-fonctions du catalogue, avec
// un interrupteur d'activation par utilisateur (prépare/simule les forfaits), le
// palier requis et le tag « à la carte ». Réutilisable par toutes les apps.
import React, { useMemo, useState } from 'react';
import { View, Text, Switch, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useApp } from '../app/AppContext';
import Feuille from '../ui/Feuille';
import SectionPliable from '../ui/SectionPliable';
import { useFonctions } from './FonctionsContext';
import { useAuth } from '../auth/AuthContext';
import { palierLabel } from './fonctionnalites';
import { useT } from '../i18n/LangueContext';

export default function FeuilleFonctions({ visible, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const app = useApp();
  const { catalogue, peutUtiliser, definirActivation, activationUtilisateur, config } = useFonctions();
  // Ces interrupteurs ne sont LUS par le moteur que si la surcharge IVV est
  // armee (regle produit). Sans elle, basculer n'a aucun effet : on ne propose
  // donc pas l'action, et on ne pretend pas qu'une fonction est « desactivee ».
  const { user, estIVV } = useAuth();
  const surchargeActive = !!estIVV && !!user?.ivvSurchargeFonctions;
  // Une ecriture refusee (regles Firestore) doit se VOIR : sans cela
  // l'interrupteur revenait en arriere sans un mot d'explication.
  const [err, setErr] = useState('');
  const basculer = (cle, v) => { setErr(''); Promise.resolve(definirActivation(cle, v)).catch(e => setErr(e?.message || String(e))); };
  const [ouverte, setOuverte] = useState(null);   // rubrique ouverte (accordéon)

  // Regroupe les racines par ORIGINE (cœur commun vs application) puis par rubrique.
  const groupes = useMemo(() => {
    const parOrigine = { core: new Map(), app: new Map() };
    for (const d of catalogue.racines) {
      const org = d.origine === 'core' ? 'core' : 'app';
      const r = d.rubrique || 'Autres';
      if (!parOrigine[org].has(r)) parOrigine[org].set(r, []);
      parOrigine[org].get(r).push(d);
    }
    return [
      { cle: 'core', titre: t('forfait.groupeCore'), rubriques: Array.from(parOrigine.core.entries()) },
      { cle: 'app', titre: t('forfait.groupeApp', { nom: app?.nom || t('forfait.cetteApp') }), rubriques: Array.from(parOrigine.app.entries()) },
    ].filter(g => g.rubriques.length > 0);
  }, [catalogue, app?.nom, t]);

  const ligne = (d, sous = false) => {
    // Palier EFFECTIF : l'administration peut forcer un palier different de
    // celui de la definition (`config/fonctionnalites.paliers`). C'est LUI que
    // le moteur applique — l'afficher evite le cas ou la feuille annonce
    // « Basic + · desactivee par toi » alors que la fonction, forcee au socle,
    // reste bel et bien active.
    const palier = (config?.paliers || {})[d.key] || d.palier || 'basic';
    // Seule une fonction VITALE resiste a la coupure (cf. moteur). Le palier
    // `socle` dit l'INCLUSION dans tous les forfaits — pas l'impossibilite de
    // couper : une fonction du socle reste desactivable pour tester.
    const socle = palier === 'socle';
    const vital = d.vital === true;
    // Etat REEL : sans surcharge, la desactivation enregistree ne s'applique pas.
    const coupeParUser = !vital && activationUtilisateur[d.key] === false;
    const actifUser = !coupeParUser || !surchargeActive;
    const utilisable = peutUtiliser(d.key);
    return (
      <View key={d.key} style={[s.ligne, sous && s.ligneSous]}>
        <Text style={s.icone}>{d.icone || (sous ? '•' : '▫️')}</Text>
        <View style={{ flex: 1 }}>
          <Text style={s.nom} numberOfLines={1}>{d.label || d.key}</Text>
          {/* À quoi sert la fonction : sans cette ligne, « Espaces » ou
              « Mes éléments » ne veulent rien dire hors contexte. */}
          {!!d.desc && <Text style={s.desc} numberOfLines={2}>{d.desc}</Text>}
          <View style={s.badges}>
            <Text style={[s.badge, s.badgePalier]}>{palierLabel(palier)}</Text>
            {d.individuel && <Text style={[s.badge, s.badgeCarte]}>{t('forfait.aLaCarte')}</Text>}
            {/* On distingue « TU l'as coupée » (neutre, réversible d'un tap) de
                « ton forfait ne l'inclut pas » (rouge) : un badge rouge sur une
                fonction du socle laissait croire à un défaut de forfait. */}
            {socle && <Text style={[s.badge, s.badgeOff]}>{t('forfait.toujoursIncluse')}</Text>}
            {vital
              ? <Text style={[s.badge, s.badgeOff]}>{t('forfait.indispensable')}</Text>
              : (coupeParUser
                ? <Text style={[s.badge, s.badgeOff]}>
                    {surchargeActive ? t('forfait.desactivee') : t('forfait.sansEffet')}
                  </Text>
                : (!utilisable && <Text style={[s.badge, s.badgeBloque]}>{t('forfait.indisponible')}</Text>))}
          </View>
        </View>
        <Switch value={actifUser} disabled={vital || !surchargeActive} onValueChange={(v) => basculer(d.key, v)}
          trackColor={{ false: UI.champBord, true: UI.accent }} thumbColor="#fff" />
      </View>
    );
  };

  return (
    <Feuille visible={visible} titre={t('forfait.fonctionsActivation')} emoji="🧩" onFermer={onFermer}>
      <Text style={s.intro}>{t('forfait.intro')}</Text>
      {!surchargeActive && <Text style={s.err}>ℹ️ {t('forfait.surchargeRequise')}</Text>}
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}
      {groupes.length === 0 && <Text style={s.vide}>{t('forfait.aucuneFonction')}</Text>}
      {groupes.map(g => (
        <View key={g.cle}>
          <Text style={s.groupeTitre}>{g.cle === 'core' ? '🧩 ' : '📱 '}{g.titre}</Text>
          {g.rubriques.map(([rubrique, defs]) => {
            const cleAcc = `${g.cle}:${rubrique}`;
            return (
              <SectionPliable key={cleAcc} titre={rubrique}
                ouvert={ouverte === cleAcc}
                onToggle={() => setOuverte(o => (o === cleAcc ? '' : cleAcc))}>
                {defs.map(d => (
                  <View key={d.key}>
                    {ligne(d)}
                    {catalogue.sousFonctionsDe(d.key).map(sf => ligne(sf, true))}
                  </View>
                ))}
              </SectionPliable>
            );
          })}
        </View>
      ))}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  intro: { fontSize: 12.5, color: UI.muted, lineHeight: 18, marginBottom: 10 },
  vide: { fontSize: 13, color: UI.faint, fontStyle: 'italic' },
  groupeTitre: { fontSize: 13, fontWeight: '900', color: UI.ink, marginTop: 14, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  ligneSous: { paddingLeft: 16 },
  icone: { fontSize: 16, width: 22, textAlign: 'center' },
  nom: { fontSize: 14, fontWeight: '700', color: UI.ink },
  badges: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 3 },
  badge: { fontSize: 10, fontWeight: '800', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 1, overflow: 'hidden' },
  badgePalier: { color: UI.accent, backgroundColor: UI.accentTint },
  badgeCarte: { color: '#8E24AA', backgroundColor: '#F3E5F5' },
  badgeBloque: { color: '#C62828', backgroundColor: '#FFEBEE' },
  badgeOff: { color: UI.muted, backgroundColor: UI.champ },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 8 },
  desc: { fontSize: 11.5, color: UI.muted, marginTop: 1, lineHeight: 15 },
});
